import React from 'react';
import {Checkbox, Radio, Text} from '../../components';
import objectAssign from 'object-assign';

/**
 * Function takes in React node and converts it into object.
 * @function
 * @param item {Node} - React node.
 * @returns {Object} - Relevant properties extracted from node.
 */
export const nodeToJs = (item) => {
  const {field, title, url} = item.props;
  return {field, title, url};
};

/**
 * Creates empty object if current value is invalid (null, undefined, string).
 *
 * @function
 * @param {object} value - Supposedly nice object, will be replaced by newly created one if invalid.
 * @returns {object} - Original object if it's actually an object or empty one.
 */
export const maybeCreateEmptyObject = (value) => {
  if (value !== null && value !== undefined && 'object' === typeof value) {
    return value;
  } else {
    return {};
  }
};

/**
 * Curried takes in data from group and data from item and composes it
 * to create data that can be fed to actual input component. This is curried
 * function: `(groupData) => (itemData) => Result`.
 * @function
 * @param groupData {Object} - Data from group.
 * @param itemData {Object} - Data specific to item.
 * @returns {Object} - Composed data.
 */
export const composeItemData = (groupData) => (itemData) => {
  const url = itemData.url;
  const gv = groupData.value;
  const field = itemData.field;
  const value = gv.hasOwnProperty(field) ? gv[field] : null;

  return {
    action: groupData.action,
    group: groupData.group,
    field: field,
    title: itemData.title,
    value: value,
    url:url
  };
};

export const setDisabled = (disabled) => (item) => objectAssign({disabled: disabled}, item);

export const numCheckboxesChecked = (sectionValue) => {
  return Object.keys(sectionValue)
    .map((itemName) => sectionValue[itemName])
    .filter((itemValue) => itemValue === true).length;
};

export const resetSectionValueTo = (sectionValue, desiredValue) => {
  const result = objectAssign({}, sectionValue);
  Object.keys(result).forEach((key) => result[key] = desiredValue);
  return result;
};

export const createTextInput = (data, index) => {
  const {
    action,
    field,
    title,
    value,
    url
  } = data;

  return (
    <Text
      action={action}
      name={field}
      title={title}
      value={value}
      key={index}
      url={url}
      />
  );
};

export const createRadio = (data, index) => {
  const {
    action,
    field,
    title,
    value,
    group
  } = data;
  return (
    <Radio
      action={action}
      title={title}
      value={value}
      key={index}
      group={group}
      field={field}
      />
  );
};

export const createCheckbox = (data, index) => {
  const {
    action,
    field,
    title,
    value,
    disabled
  } = data;
  return (
    <Checkbox
      action={action}
      name={field}
      title={title}
      value={value}
      disabled={disabled && value !== true}
      key={index}
      />
  );
};

export const computeSectionValue = (value) => {
  return Object.keys(value)
    .map((itemName) => ({key: itemName, value: value[itemName]}))
    .filter((item) => item.value === true)
    .reduce(
      (container, property) => {
        container[property.key] = property.value;
        return container;
      },
      {});
};
